import {fillArray} from '@benchling/js-utils';

import {assert} from 'Asserts';
import {
  ExperimentalInputs,
  ExperimentalInputTypedValuesByComponentName,
} from 'procedures/planning/ProcedurePlanTypes';
import {buildValidationStatesForUpdate} from 'studies/typed/shared/procedure-runs/conditions/conditionUtils';
import {
  Condition,
  ConditionGroup,
} from 'studies/typed/shared/procedure-runs/design-state/useExperimentDesignState';

import {ExperimentMethod} from './ExperimentalInputs';
import {conditionGroupFromRow, getSharedConditionGroups} from './sharedPlanConditionView';

/**
 * Local condition rows for each flowchart method. Position is the array index within one method.
 * Every flowchart method is a key, in flowchart order, and methods with no rows map to an empty array.
 * The helpers below take that completeness as given and read the method ids from the keys.
 */
export type PlanConditionsByMethodId = Record<string, Array<Condition>>;

/**
 * A new unsaved row with the default name and one replicate.
 */
export function makeDefaultPlanCondition(): Condition {
  return {name: 'Condition 1', numReplicates: 1};
}

/**
 * One unsaved row on every method, used when a plan has no stored conditions.
 */
export function getDefaultPlanConditionsForMethods(
  methodIds: ReadonlyArray<string>
): PlanConditionsByMethodId {
  return Object.fromEntries(methodIds.map((methodId) => [methodId, [makeDefaultPlanCondition()]]));
}

/**
 * Recomputes name and description validation independently for each method.
 */
export function withPlanConditionValidationStates(
  planConditionsByMethodId: PlanConditionsByMethodId
): PlanConditionsByMethodId {
  return Object.fromEntries(
    Object.entries(planConditionsByMethodId).map(([methodId, rows]) => {
      const validationStates = buildValidationStatesForUpdate(rows);
      return [methodId, rows.map((row, index) => ({...row, validationState: validationStates[index]}))];
    })
  );
}

/**
 * Makes each method's `values` (one entry per condition) the same length as that method's conditions in
 * `planConditionsByMethodId`. An entry keeps its value at the same index when there is one, and takes
 * the method's `defaultValues` otherwise. A method with no conditions gets an empty `values`.
 *
 * Callers need this when the conditions and the inputs are built separately and can disagree on length:
 * - Empty plan load: the loader adds one default condition per method, but no `values` are stored.
 * - Procedure change: the conditions carry over to the new procedure's methods, whose inputs start with
 *   an empty `values`.
 * - Version update: the remapped conditions can have different row counts than the preserved inputs,
 *   which are built without any rows.
 */
export function resizeExperimentalInputsToPlanConditions(
  planConditionsByMethodId: PlanConditionsByMethodId,
  experimentalInputs: ExperimentalInputs
): ExperimentalInputs {
  return Object.fromEntries(
    Object.entries(planConditionsByMethodId).map(([methodId, rows]) => {
      const inputs = experimentalInputs[methodId];
      assert(inputs != null, `Expected experimental inputs for method ${methodId}`);
      return [
        methodId,
        {...inputs, values: fillArray(rows.length, (index) => inputs.values[index] ?? inputs.defaultValues)},
      ];
    })
  );
}

/** Flowchart condition groups plus the inputs indexed to line up with those groups. */
export interface PlanFlowchartConditionGroups {
  conditionGroups: Array<ConditionGroup>;
  conditionInputs: ExperimentalInputs;
}

/**
 * Flowchart condition groups and group-aligned inputs for a plan. Uniform plans collapse to one
 * condition group per shared row across all methods. Non-uniform plans expand to one condition group
 * per (method, row), where each group covers a single method and `conditionInputs` shows that
 * method's value at the group's row and its defaults for the other methods.
 *
 * Non-uniform groups carry `conditionIndexForNodeId` set to the method-local row index so flowchart
 * node ids stay stable when a row is inserted into an earlier method (which shifts array positions).
 */
export function planFlowchartConditionGroups(
  planConditionsByMethodId: PlanConditionsByMethodId,
  methods: ReadonlyArray<ExperimentMethod>,
  experimentalInputs: ExperimentalInputs
): PlanFlowchartConditionGroups {
  const uniformConditionGroups = getSharedConditionGroups(planConditionsByMethodId);
  if (uniformConditionGroups != null) {
    return {conditionGroups: uniformConditionGroups, conditionInputs: experimentalInputs};
  }

  const conditionGroupMeta: Array<{methodId: string; rowIndex: number}> = [];
  const conditionGroups: Array<ConditionGroup> = [];
  methods.forEach((method) => {
    (planConditionsByMethodId[method.id] ?? []).forEach((row, rowIndex) => {
      conditionGroupMeta.push({methodId: method.id, rowIndex});
      conditionGroups.push({
        ...conditionGroupFromRow(row, {[method.id]: row.id ?? null}),
        conditionIndexForNodeId: rowIndex,
      });
    });
  });

  const conditionInputs: ExperimentalInputs = {};
  methods.forEach((method) => {
    const inputs = experimentalInputs[method.id];
    if (inputs == null) {
      return;
    }
    conditionInputs[method.id] = {
      ...inputs,
      values: conditionGroupMeta.map((meta) =>
        meta.methodId === method.id
          ? (inputs.values[meta.rowIndex] ?? inputs.defaultValues)
          : inputs.defaultValues
      ),
    };
  });
  return {conditionGroups, conditionInputs};
}

/**
 * Copies each existing `values` entry to its new index after a method's rows are edited, so a row keeps
 * its values when rows are added, removed, or reordered. `prevIndexes[i]` is the index, before the edit,
 * of the entry that the new row at `i` takes its values from, or null when that row is new and takes the
 * method's default values.
 */
function remapValuesByPrevIndexes(
  values: ReadonlyArray<ExperimentalInputTypedValuesByComponentName>,
  prevIndexes: ReadonlyArray<number | null>,
  defaultValues: ExperimentalInputTypedValuesByComponentName
): Array<ExperimentalInputTypedValuesByComponentName> {
  return prevIndexes.map((index) => (index != null && values[index] != null ? values[index] : defaultValues));
}

interface PlanDesignEdit {
  planConditionsByMethodId: PlanConditionsByMethodId;
  experimentalInputs: ExperimentalInputs;
}

function remapMethodInputs(
  inputs: ExperimentalInputs[string],
  prevIndexes: ReadonlyArray<number | null>
): ExperimentalInputs[string] {
  return {...inputs, values: remapValuesByPrevIndexes(inputs.values, prevIndexes, inputs.defaultValues)};
}

/**
 * Replaces one method's rows and remaps only that method's `values` with `prevIndexes`.
 * Other methods keep their rows and columns.
 */
export function withMethodConditions(
  planConditionsByMethodId: PlanConditionsByMethodId,
  experimentalInputs: ExperimentalInputs,
  methodId: string,
  rows: Array<Condition>,
  prevIndexes: ReadonlyArray<number | null>
): PlanDesignEdit {
  const inputs = experimentalInputs[methodId];
  assert(inputs != null, `Expected experimental inputs for method ${methodId}`);
  return {
    planConditionsByMethodId: withPlanConditionValidationStates({
      ...planConditionsByMethodId,
      [methodId]: rows,
    }),
    experimentalInputs: {...experimentalInputs, [methodId]: remapMethodInputs(inputs, prevIndexes)},
  };
}

/**
 * Writes the same name, description, and replicate count onto every method.
 * Each method keeps the id of its previous row at `prevIndexes`, and every method's input
 * `values` are remapped with that same index list.
 */
export function withSharedConditions(
  planConditionsByMethodId: PlanConditionsByMethodId,
  experimentalInputs: ExperimentalInputs,
  rows: ReadonlyArray<Condition>,
  prevIndexes: ReadonlyArray<number | null>
): PlanDesignEdit {
  const nextRowsByMethodId: PlanConditionsByMethodId = {};
  const nextInputs: ExperimentalInputs = {};
  Object.entries(planConditionsByMethodId).forEach(([methodId, previousRows]) => {
    nextRowsByMethodId[methodId] = rows.map((row, index) => {
      const previousIndex = prevIndexes[index];
      const previousRow = previousIndex != null ? previousRows[previousIndex] : undefined;
      return {
        name: row.name,
        numReplicates: row.numReplicates,
        ...(row.description !== undefined ? {description: row.description} : {}),
        ...(previousRow?.id != null ? {id: previousRow.id} : {}),
      };
    });
    const inputs = experimentalInputs[methodId];
    assert(inputs != null, `Expected experimental inputs for method ${methodId}`);
    nextInputs[methodId] = remapMethodInputs(inputs, prevIndexes);
  });
  return {
    planConditionsByMethodId: withPlanConditionValidationStates(nextRowsByMethodId),
    experimentalInputs: nextInputs,
  };
}
